import { Component } from '@angular/core';
import { ProductModel } from 'src/app/core/models/product.model';
import { ProductService } from './product.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ProductCreateUpdateDeleteComponent } from './product-create-update-delete/product-create-update-delete.component';
import { FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { PRODUCT_FILTER_SCHEMA } from './product-filter/product-filter.schema';
import { CrudModeEnum } from 'src/app/core/base/enum/crud-mode.enum';
import { Router } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { TvaModel } from '../../../../core/models/tva.model';
import { TvaService } from '../../settings/tva/tva.service';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';

@Component({
  selector: 'vex-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss'],
})
export class ProductComponent extends DataTableBase<ProductModel> {
  protected override filterSchema = PRODUCT_FILTER_SCHEMA;

  feisColumns: Array<FeiColumn> = [
    { label: 'Type', column: 'quoteId', type: 'custom' },
    { label: 'Réf. Fournisseur', column: 'reference', type: 'custom', canSort: true },
    { label: 'Fournisseurs', column: 'supplier', propertyDisplay: 'supplier.label', width: '20%', wrap: true },
    { label: 'Article générique', column: 'genericProduct', propertyDisplay: 'genericProduct.label' },
    { label: 'Libellé', column: 'label', type: 'custom', width: '300px', wrap: true },
    { label: 'Marque/Fabricant', column: 'manufacturer' },
    { label: 'Conditionnement', column: 'conditioningUnit' },
    { label: 'UA', column: 'purchaseUnit' },
    { label: 'Prix H.T', column: 'priceHT', additionalDisplay: ' €', type: 'number' },
    { label: 'Taux TVA', column: 'tva', type: 'custom' },
    { label: 'Prix TTC', column: 'priceTTC', additionalDisplay: ' €', type: 'number' },
    { label: 'Visibilité e-Shop', column: 'isSuspended', type: 'eshopStatus' },
    {
      label: '',
      column: 'actions',
      canDelete: false,
      canArchive: false,
      canUpdate: false,
      canCreate: false,
      // PRED-3230 : la suspension e-Shop est une modification de l'article.
      canSuspend: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_PRODUCT),
    }, // Pred 1283
  ];

  canShowArchive = true;
  actionText = 'Expiré';
  showComment;
  showPreviousReference;
  filters: any = {
    isExpired: false,
  };

  constructor(
    public service: ProductService,
    private dialog: MatDialog,
    private router: Router,
    private tvaService: TvaService,
    public authService: AuthService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
  }

  /** Flag technique du fetch, hors `this.filters` : jamais dans l'URL ni effacé par le sidenav. */
  protected override buildFilters(): Record<string, unknown> {
    return { ...super.buildFilters(), isInPurchaseFamily: true };
  }

  getTvaRate(tva: TvaModel) {
    return `${this.tvaService.getRateByDate(tva).rate.toFixed(2)}%`;
  }
  hasPermission() {
    return this.authService.isReferentEngagement();
  }

  rowClickItem(element: ProductModel): void {
    this.readItemForUserPredik(element);
  }

  readItem(element: ProductModel): void {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Read,
        },
      })
      .afterClosed()
      .subscribe((product: ProductModel) => {
        if (product) {
          this.initData();
        }
      });
  }

  readItemForUserPredik(element: ProductModel): void {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
          userPredikMode: true,
        },
      })
      .afterClosed()
      .subscribe((product: ProductModel) => {
        if (product) {
          this.initData();
        }
      });
  }

  createItem(): void {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent)
      .afterClosed()
      .subscribe((product: ProductModel) => {
        if (product) {
          if (product._id) {
            //     this.snackbarService.warning('Cette nouvelle référence sera accessible au catalogue après validation d’un administrateur.');
            this.router.navigateByUrl('/management/product/' + product._id).then();
          } else {
            this.initData();
          }
        }
      });
  }

  updateItem(element: ProductModel): void {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((product: ProductModel) => {
        if (product) {
          this.initData();
        }
      });
  }

  deleteItem(element: ProductModel) {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.technicalIdentifier}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((product: ProductModel) => {
        if (product) {
          this.initData();
        }
      });
  }

  archiveItem(element: ProductModel) {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.technicalIdentifier}`,
          mode: CrudModeEnum.Archive,
        },
      })
      .afterClosed()
      .subscribe((product: ProductModel) => {
        if (product) {
          this.initData();
        }
      });
  }

  suspendItem(element: ProductModel) {
    const call$ = element.isSuspended ? this.service.unsuspend(element) : this.service.suspend(element);
    call$.subscribe(() => this.initData());
  }

  redirectToElement(element: ProductModel) {
    if (element.quoteId) {
      this.router.navigateByUrl('/achat/quote/' + element.quoteId).then();
      return;
    } else {
      this.router.navigateByUrl('/management/engagement/' + element.contractId).then();
    }
  }

  importItems() {
    this.dialog
      .open(ProductCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((product: ProductModel[]) => {
        if (product) {
          this.initData();
        }
      });
  }
}
